import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../firebase/AuthProvider';
import { Conversation, MeetingRoomDto, RemoveAdHocFromConversationDto } from '../../../services/chatService/ChatService';
import { beginMeetingTiming, GetExistingOrCretateMeetingAsync, InitiateVideoCallNotifyAsync, markMeetingTiming } from '../../../services/videoService/VideoService';
import { LobbyState } from '../../../interfaces/videocallDtos/videoCall';

import { ValidateOtpResponseDto } from '../../../interfaces/ExtermalUserDtos/ExternalUsersDtos';
import ChatParticipantModal from './modals/ChatParticipantModal';
import { encodeLobbyStateToToken } from '../../../utilities/lobbyStateToken';
import { Video, Users, ArrowLeft, Loader2, Hash, Archive, Bot, Search, Bookmark } from 'lucide-react';
import Tooltip from '../../ui/Tooltip/Tooltip';
import { useChatConnection } from '../../../contexts/ChatConnectionContext';
import MilestoneAvatar from '../../ui/Avatar/MilestoneAvatar';
import { isAutomatedBotConversation } from '../../../utilities/chat/conversationUtils';



const getParticipantLabel = (participant: any): string => {
    if (!participant) return 'Unknown';
    const explicitName = [participant.firstName, participant.lastName].filter(Boolean).join(' ').trim();
    return (explicitName || participant.displayName || participant.email || participant.userName || 'Unknown').trim();
};

const compactParticipantLabel = (participant: any): string => {
    const rawLabel = getParticipantLabel(participant);
    if (rawLabel.includes('@')) {
        const localPart = rawLabel.split('@')[0] || rawLabel;
        return localPart.length > 16 ? `${localPart.slice(0, 15)}…` : localPart;
    }
    if (rawLabel.length <= 18) return rawLabel;
    const words = rawLabel.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        const abbreviated = `${words[0].charAt(0)}. ${words[words.length - 1]}`;
        if (abbreviated.length <= 18) return abbreviated;
    }
    return `${rawLabel.slice(0, 17)}…`;
};

const getCompactConversationTitle = (participants: any[]): string => {
    if (!participants?.length) return 'Conversation';
    if (participants.length === 1) return getParticipantLabel(participants[0]);
    const firstParticipant = compactParticipantLabel(participants[0]);
    const otherCount = participants.length - 1;
    return `${firstParticipant} & ${otherCount} ${otherCount === 1 ? 'other' : 'others'}`;
};

type ChatAppearance = 'default' | 'meeting-dark';

type ConversationCallStatePayload = {
    conversationId?: number;
    ConversationId?: number;
    roomId?: string;
    RoomId?: string;
    status?: string;
    Status?: string;
    startTime?: string | Date;
    StartTime?: string | Date;
};

const activeMeetingFromCallState = (payload: ConversationCallStatePayload): MeetingRoomDto | undefined => {
    const status = String(payload.status ?? payload.Status ?? '').toLowerCase();
    if (status === 'end' || status === 'ended') return undefined;

    const roomId = payload.roomId ?? payload.RoomId;
    if (!roomId) return undefined;

    const start = new Date(payload.startTime ?? payload.StartTime ?? Date.now());
    return {
        id: roomId,
        roomId,
        createdAt: start,
        startDate: start,
        title: 'Call in progress',
    };
};

interface ChatHeaderProps {
    conversation: Conversation;
    handleChatClose?: () => void;
    singlePane?: boolean;
    isShowCallingIcon?: boolean;
    externalUserDetails?: ValidateOtpResponseDto | null;
    isSideView?: boolean;
    appearance?: ChatAppearance;
    onOpenSearch?: () => void;
    onOpenBookmarks?: () => void;
    activeUtility?: "search" | "bookmarks" | null;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
    conversation,
    handleChatClose,
    singlePane,
    isShowCallingIcon = true,
    externalUserDetails = null,
    isSideView = false,
    appearance = 'default',
    onOpenSearch,
    onOpenBookmarks,
    activeUtility = null,
}) => {
    const { currentUser } = useAuth();
    const { connection } = useChatConnection();
    const [callLoading, setCallLoading] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [activeMeeting, setActiveMeeting] = useState<MeetingRoomDto | undefined>(conversation.activeMeeting);
    const navigate = useNavigate();
    const isMeetingDark = appearance === 'meeting-dark';

    useEffect(() => {
        setActiveMeeting(conversation.activeMeeting);
    }, [conversation.activeMeeting, conversation.id]);

    useEffect(() => {
        if (!connection) return;

        const handleCallStateChanged = (payload: ConversationCallStatePayload) => {
            const conversationId = payload.conversationId ?? payload.ConversationId;
            if (conversationId !== conversation.id) return;
            setActiveMeeting(activeMeetingFromCallState(payload));
        };

        const handleMeetingRemoved = (payload: RemoveAdHocFromConversationDto) => {
            if (payload.conversationId === conversation.id) setActiveMeeting(undefined);
        };

        connection.on('ConversationCallStateChanged', handleCallStateChanged);
        connection.on('RemoveAdHocFromConversation', handleMeetingRemoved);
        return () => {
            connection.off('ConversationCallStateChanged', handleCallStateChanged);
            connection.off('RemoveAdHocFromConversation', handleMeetingRemoved);
        };
    }, [connection, conversation.id]);

    const currentViewerId = externalUserDetails?.externalUserDetailId ?? currentUser?.uid;
    const otherParticipants = conversation.participants.filter(
        participant => participant.id !== currentViewerId
    );
    const isChannelConversation = conversation.isChannel === true ||
        (conversation.isChannel === undefined && Boolean(conversation.channel));
    const isBotConversation = isAutomatedBotConversation(conversation, currentViewerId);
    const conversationTitle = isChannelConversation && conversation.channel?.name
        ? conversation.channel.name
        : isBotConversation
        ? 'Waivera'
        : getCompactConversationTitle(otherParticipants);
    const isGroupConversation = !isChannelConversation && otherParticipants.length > 1;
    const directParticipant = !isChannelConversation && otherParticipants.length === 1 ? otherParticipants[0] : undefined;
    const displayAvatarForId = directParticipant?.id;

    const navigateToMeeting = (roomId: string, isPublisher: boolean, calendarEventId?: string) => {
        const state: LobbyState = {
            calenderEventId: calendarEventId ?? null,
            relatedChatConversationId: conversation.id.toString(),
            isPublisher,
            isOneToOneCall: conversation.participants.length === 2,
        };
        const stateToken = encodeLobbyStateToToken(state);
        navigate(`/lobby/${roomId}?state=${encodeURIComponent(stateToken)}`);
    };

    const handleClickVideoCall = async () => {
        if (!conversation.id) return;

        // Joining an already-running conversation call is intentionally silent.
        // Do not create another call invitation or ringtone cycle.
        if (activeMeeting?.roomId) {
            navigateToMeeting(activeMeeting.roomId, false, activeMeeting.calendarEventId);
            return;
        }

        beginMeetingTiming();
        const authToken = currentUser ? await currentUser.getIdToken() : '';
        setCallLoading(true);
        try {
            const res = await GetExistingOrCretateMeetingAsync(authToken, null, conversation.id.toString());
            markMeetingTiming("room.ready");
            if (!res.success) {
                toast.error(res.message);
                return;
            }

            // The API returns isPublisher=false when a real publisher already exists.
            // Only a genuinely new call should enter the initiator/ringing path.
            // Persist the call generation before entering the lobby. Admission and host
            // control checks therefore never need to fail open while the publisher is
            // still negotiating its Janus session. A second initiate call after publish
            // binds that session idempotently without ringing participants twice.
            if (res.data.isPublisher) {
                await InitiateVideoCallNotifyAsync(authToken, {
                    targetUserId: null,
                    relatedChatConversationId: conversation.id,
                    calenderEventId: null,
                    roomId: res.data.roomId,
                    prepareOnly: true,
                });
                markMeetingTiming("call.prepared");
            }
            navigateToMeeting(res.data.roomId, res.data.isPublisher, (res.data as any).calendarEventId);
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setCallLoading(false);
        }
    };

    const containerClass = isMeetingDark
        ? 'border-slate-800 bg-slate-900 text-slate-100'
        : 'border-slate-200 bg-white text-slate-900 dark:border-transparent dark:bg-[#171a24] dark:text-slate-100';

    return (
        <>
            <div className={`dark-surface flex shrink-0 items-center justify-between border-b ${containerClass} ${singlePane ? 'min-h-[50px] px-2.5 py-1.5' : 'min-h-[56px] px-3 py-2'}`}>
                <div className="flex min-w-0 items-center gap-2">
                    {singlePane && (
                        <Tooltip content="Back to messages">
                            <button
                                type="button"
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${isMeetingDark ? 'bg-slate-800 text-slate-300 hover:text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-[#7E57C2] dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'}`}
                                onClick={handleChatClose}
                                aria-label="Back to messages"
                            >
                                <ArrowLeft size={18} strokeWidth={2.2} />
                            </button>
                        </Tooltip>
                    )}

                    {isBotConversation ? (
                        <div
                            className={`flex ${singlePane ? 'h-8 w-8' : 'h-9 w-9'} shrink-0 items-center justify-center rounded-xl ${isMeetingDark ? 'bg-[#7E57C2]/20 text-purple-300' : 'bg-[#7E57C2]/10 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300'}`}
                            title="Automated Waivera notifications"
                            aria-label="Automated bot conversation"
                        >
                            <Bot size={singlePane ? 18 : 20} strokeWidth={2.1} />
                        </div>
                    ) : isChannelConversation ? (
                        <div className={`flex ${singlePane ? 'h-8 w-8' : 'h-9 w-9'} shrink-0 items-center justify-center rounded-xl ${conversation.channel?.isArchived ? 'bg-amber-50 text-amber-600' : isMeetingDark ? 'bg-[#7E57C2]/20 text-purple-300' : 'bg-purple-50 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300'}`}>
                            {conversation.channel?.isArchived ? <Archive size={18} /> : <Hash size={18} />}
                        </div>
                    ) : isGroupConversation ? (
                        <div className={`flex ${singlePane ? 'h-8 w-8' : 'h-9 w-9'} shrink-0 items-center justify-center rounded-xl ${isMeetingDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'}`}>
                            <Users size={18} />
                        </div>
                    ) : (
                        <div className={`flex ${singlePane ? 'h-8 w-8' : 'h-9 w-9'} shrink-0 items-center justify-center`}>
                            <MilestoneAvatar
                                size={singlePane ? 'xxs' : 'xs'}
                                userId={displayAvatarForId || ''}
                                displayName={directParticipant?.displayName || `${directParticipant?.firstName || ''} ${directParticipant?.lastName || ''}`.trim() || directParticipant?.email}
                                alternate
                            />
                        </div>
                    )}

                    <div className="min-w-0">
                        <h2 className={`m-0 truncate text-[13.5px] font-semibold leading-5 ${isMeetingDark ? 'text-white' : 'text-slate-900 dark:text-slate-100'}`}>
                            {conversationTitle}
                        </h2>
                        {isBotConversation ? (
                            <div className={`mt-0.5 flex items-center gap-1 text-[11px] font-medium ${isMeetingDark ? 'text-purple-300' : 'text-[#7E57C2]'}`}>
                                <Bot size={12.5} />
                                <span>Automated notifications</span>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowUsersModal(true)}
                                className={`mt-0 flex items-center gap-1 border-none bg-transparent p-0 text-[10.5px] font-medium transition-colors ${isMeetingDark ? 'text-slate-300 hover:text-purple-300' : 'text-slate-500 hover:text-[#7E57C2] dark:text-slate-400 dark:hover:text-purple-300'}`}
                            >
                                <Users size={12.5} />
                                <span>{conversation.participants.length} {conversation.participants.length === 1 ? 'member' : 'members'}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="ml-2 flex shrink-0 items-center gap-1">
                {onOpenSearch && (
                    <Tooltip content="Find in conversation">
                        <button
                            type="button"
                            onClick={onOpenSearch}
                            aria-label="Find in conversation"
                            aria-pressed={activeUtility === "search"}
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${activeUtility === "search" ? 'bg-purple-100 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300' : isMeetingDark ? 'bg-slate-800 text-slate-300 hover:text-purple-300' : 'bg-slate-100 text-slate-600 hover:bg-purple-50 hover:text-[#7E57C2] dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-purple-300'}`}
                        >
                            <Search size={17} />
                        </button>
                    </Tooltip>
                )}
                {onOpenBookmarks && (
                    <Tooltip content="Saved messages">
                        <button
                            type="button"
                            onClick={onOpenBookmarks}
                            aria-label="Saved messages"
                            aria-pressed={activeUtility === "bookmarks"}
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${activeUtility === "bookmarks" ? 'bg-purple-100 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300' : isMeetingDark ? 'bg-slate-800 text-slate-300 hover:text-purple-300' : 'bg-slate-100 text-slate-600 hover:bg-purple-50 hover:text-[#7E57C2] dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-purple-300'}`}
                        >
                            <Bookmark size={16} fill={activeUtility === "bookmarks" ? "currentColor" : "none"} />
                        </button>
                    </Tooltip>
                )}
                {isShowCallingIcon && (
                    activeMeeting?.roomId ? (
                        <button
                            type="button"
                            onClick={handleClickVideoCall}
                            className={`ml-1 inline-flex h-8 max-w-[130px] shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold transition-colors ${isMeetingDark ? 'bg-[#7E57C2]/15 text-purple-200 hover:bg-[#7E57C2]/25' : 'bg-[#7E57C2]/10 text-[#6847A3] hover:bg-[#7E57C2]/15 dark:bg-purple-400/15 dark:text-purple-200'}`}
                            aria-label="Join active call"
                        >
                            <span className="relative flex h-2 w-2 shrink-0">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7E57C2] opacity-40" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7E57C2]" />
                            </span>
                            <Video size={15} className="shrink-0" />
                            <span className="whitespace-nowrap">{singlePane ? "Join" : "Join call"}</span>
                        </button>
                    ) : (
                        <Tooltip content="Start video call">
                            <button
                                type="button"
                                className={`ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${isMeetingDark ? 'bg-slate-800 text-slate-300 hover:text-purple-300' : 'bg-slate-100 text-slate-600 hover:bg-purple-50 hover:text-[#7E57C2] dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-purple-300'}`}
                                onClick={handleClickVideoCall}
                                disabled={callLoading}
                                aria-label="Start video call"
                            >
                                {callLoading ? <Loader2 className="animate-spin" size={17} /> : <Video size={17} />}
                            </button>
                        </Tooltip>
                    )
                )}
                </div>
            </div>

            <ChatParticipantModal
                handleCloseUsersModal={() => setShowUsersModal(false)}
                participants={conversation.participants}
                showUsersModal={showUsersModal}
                isSideView={isSideView}
            />
        </>
    );
};

export default ChatHeader;
